import React, { useState, useEffect } from 'react';
import contentData from '../../content/content.json';

const Admin = () => {
  const [content, setContent] = useState({});
  const [selectedSection, setSelectedSection] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const backupContent = localStorage.getItem('contentBackup');
    if (backupContent) {
      try {
        const parsedBackup = JSON.parse(backupContent);
        setContent(parsedBackup);
      } catch (error) {
        console.error('Error parsing backup content:', error);
        setContent(contentData);
      }
    } else {
      setContent(contentData);
    }
  }, []);

  const handleSectionSelect = (section) => {
    setSelectedSection(section);
    setEditedContent(JSON.stringify(content[section], null, 2));
    setEditMode(true);
  };

  const handleSave = () => {
    try {
      const parsedContent = JSON.parse(editedContent);
      const newContent = { ...content, [selectedSection]: parsedContent };
      setContent(newContent);

      // Save to localStorage for persistence in browser
      localStorage.setItem('contentBackup', JSON.stringify(newContent));

      // Auto-download the updated JSON file
      const dataStr = JSON.stringify(newContent, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);

      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', 'content.json');
      document.body.appendChild(linkElement);
      linkElement.click();
      document.body.removeChild(linkElement);

      alert(`Content for "${selectedSection}" saved and downloaded! Replace the content.json file in your project with the downloaded file.`);

      setEditMode(false);
      setSelectedSection('');
    } catch (error) {
      alert('Invalid JSON format. Please check your syntax.');
      console.error('JSON parse error:', error);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setSelectedSection('');
    setEditedContent('');
  };

  const handleDownload = () => {
    const dataStr = JSON.stringify(content, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);

    const exportFileDefaultName = 'content.json';

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const filteredSections = Object.keys(content).filter(section =>
    section.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-container p-6 max-w-7xl mx-auto bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Content Management Admin</h1>
        <p className="text-gray-600">Edit content and download updated JSON files for your application</p>
      </div>

      {!editMode ? (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <div className="flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search sections..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  localStorage.removeItem('contentBackup');
                  setContent(contentData);
                  alert('Reset to original content!');
                }}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset
              </button>
              <button
                onClick={handleDownload}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Full JSON
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredSections.map((section) => (
              <div
                key={section}
                className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all duration-200 transform hover:-translate-y-1"
                onClick={() => handleSectionSelect(section)}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-800 capitalize">
                    {section.replace(/([A-Z])/g, ' $1').trim()}
                  </h3>
                  <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {Array.isArray(content[section])
                      ? 'Array'
                      : typeof content[section] === 'object'
                      ? 'Object'
                      : 'Value'
                    }
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  {Array.isArray(content[section])
                    ? `${content[section].length} items`
                    : typeof content[section] === 'object'
                    ? `${Object.keys(content[section]).length} properties`
                    : String(content[section]).substring(0, 50) + (String(content[section]).length > 50 ? '...' : '')
                  }
                </p>
                <div className="text-blue-600 text-sm font-medium">
                  Click to edit →
                </div>
              </div>
            ))}
          </div>

          {filteredSections.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No sections found matching "{searchTerm}"</p>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800 capitalize">
              Edit {selectedSection.replace(/([A-Z])/g, ' $1').trim()}
            </h2>
            <button
              onClick={handleCancel}
              className="text-gray-500 hover:text-gray-700 p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              JSON Content
            </label>
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="w-full h-96 p-4 border border-gray-300 rounded-lg font-mono text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
              placeholder="Enter JSON content..."
              spellCheck="false"
            />
          </div>

          <div className="flex gap-4">
            <button
              onClick={handleSave}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Save Changes
            </button>
            <button
              onClick={handleCancel}
              className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200"
            >
              Cancel
            </button>
          </div>

          {/* <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h4 className="text-amber-800 font-medium">Manual File Replacement Required</h4>
                <p className="text-amber-700 text-sm mt-1">
                  Changes are automatically downloaded as a JSON file. To apply them permanently:
                </p>
                <ol className="text-amber-700 text-sm mt-2 list-decimal list-inside space-y-1">
                  <li>Save the downloaded <code className="bg-amber-100 px-1 rounded">content.json</code> file</li>
                  <li>Replace the existing <code className="bg-amber-100 px-1 rounded">src/content/content.json</code> in your project</li>
                  <li>Rebuild the application with <code className="bg-amber-100 px-1 rounded">npm run build</code></li>
                </ol>
              </div>
            </div>
          </div> */}
        </div>
      )}
    </div>
  );
};

export default Admin;